import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import cn from 'classnames';

import {
	AdaptiveSingleLineText,
	DynamicIconWrapper,
	Icons,
} from '@components/common';
import TextAvatar from '@components/common/TextAvatar';

import { useQueryUser } from '@hooks/useQueryUser';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { clearThreadsDataFromLocalstorage } from '@utils/kaiAssistants';
import QueryClientService from '@utils/queryClientService';

import { routes } from '@constants/routes';

import styles from './UserMenu.module.scss';

const { userProfile } = routes;

interface IUserMenuProps {
	hideMapControls: () => void;
}

const UserMenu = ({
	hideMapControls,
}: IUserMenuProps) => {
	const { keycloak, initialized } = useKeycloak();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { accessPermission } = useAppSelector(auth);

	const { platformOwnerAdministrator } = accessPermission;

	const { organisationName, user } = useQueryUser();

	const userMenuWrapperRef = useRef<HTMLDivElement | null>(null);
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

	const listOfRoles = user?.roles ? user.roles.map(({ title }) => title) : [];

	const stringListOfRoles = listOfRoles.join(', ');

	const [isActiveLogOutIcon, setIsActiveLogOutIcon] = useState(false);

	const [isActiveUserProfileIcon, setIsActiveUserProfileIcon] = useState(pathname.includes(userProfile.index));

	const menuClasses = cn(styles.menu, {
		[styles.showAppMenu]: isUserMenuOpen,
		[styles.signOut]: platformOwnerAdministrator,
	});

	const toggleMenu = useCallback(() => {
		hideMapControls();
		setIsUserMenuOpen(!isUserMenuOpen);
	}, [hideMapControls, setIsUserMenuOpen, isUserMenuOpen]);

	const closeMenu = () => setIsUserMenuOpen(false);

	useOnClickOutside({ refs: [userMenuWrapperRef], handler: () => closeMenu() });

	const handleMouseEnterLogOut = () => setIsActiveLogOutIcon(true);

	const handleMouseEnterUserProfile = () => setIsActiveUserProfileIcon(true);

	const handleMouseLeaveLogOut = () => setIsActiveLogOutIcon(false);

	const handleMouseLeaveUserProfile = () => {
		if (pathname.includes(userProfile.index)) return;

		setIsActiveUserProfileIcon(false);
	};

	const onClickLogOutHandler = async () => {
		if (!initialized || !keycloak || !keycloak.authenticated) return;

		try {
			window.history.replaceState('', '', '/');

			sessionStorage.clear();

			await keycloak.logout();

			// clear kai assistants threads data from localstorage
			clearThreadsDataFromLocalstorage();

			// clear all queries caches
			QueryClientService.queryClient.clear();
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	};

	const onClickUserProfile = () => navigate(userProfile.index);

	useEffect(() => {
		setIsActiveUserProfileIcon(pathname.includes(userProfile.index));
	}, [pathname]);

	return (
		<div
			ref={userMenuWrapperRef}
			className={styles.userMenuContainer}
			data-testid='user-menu-wrapper'
		>
			{user?.id && (
				<TextAvatar
					avatarId={user.avatar?.id}
					avatarStyles={{ top: '4px' }}
					firstName={user?.firstName}
					fontSize='16px'
					id={user.id}
					lastName={user?.lastName}
					onClick={toggleMenu}
					size='36px'
				/>
			)}
			<div className={menuClasses}>
				<div className={styles.userData}>
					<AdaptiveSingleLineText
						className={styles.name}
						maxWidth={186}
						text={user?.firstName + ' ' + user?.lastName}
					/>
					{!!listOfRoles.length && isUserMenuOpen
						&& <AdaptiveSingleLineText
							className={styles.roles}
							maxWidth={186}
							text={stringListOfRoles}
						/>
					}
					{organisationName
						&& <AdaptiveSingleLineText
							className={styles.organisation}
							maxWidth={186}
							text={organisationName}
						/>
					}
					{!platformOwnerAdministrator
						&& <div
							className={styles.menuItem}
							onClick={onClickUserProfile}
							onMouseEnter={handleMouseEnterUserProfile}
							onMouseLeave={handleMouseLeaveUserProfile}
						>
							<DynamicIconWrapper
								icon={Icons.userIcon}
								isActive={isActiveUserProfileIcon}
							/>
							<span>Profile</span>
						</div>
					}
					<div
						className={styles.menuItem}
						onClick={onClickLogOutHandler}
						onMouseEnter={handleMouseEnterLogOut}
						onMouseLeave={handleMouseLeaveLogOut}
					>
						<DynamicIconWrapper
							icon={Icons.logOutIcon}
							isActive={isActiveLogOutIcon}
						/>
						<span>Sign out</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default memo(UserMenu);
